/**
 * @license
 * Copyright 2022 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { LEGAL_ORIGIN as OPERATOR_LEGAL_ORIGIN } from '@aglyn/aglyn/app-utils/published-legal-pages'
import { buildDocsUrl } from './docs-links'

export const CONTENT_MAX_WIDTH = 'xl'
export const DRAWER_WIDTH = 290
export const TAB_HEIGHT = 40
export const TOP_BAR_HEIGHT = 48
/*
 * Declared beside `ListRowActions`, whose centring depends on it, and
 * re-exported here so the console's other layout numbers still read together.
 */
export { TABLE_ROW_HEIGHT } from '@aglyn/shared-ui-jsx/const/table-pagination'
/**
 * Table header height, shared by the DataTable (layouts, components,
 * templates) and the bespoke screens hierarchy table. MUI's size="small"
 * TableHead and MUI X's DataGrid column header default to different heights,
 * so the console showed two table designs until both were pinned here.
 */
export const TABLE_HEAD_HEIGHT = 48

/**
 * A short form field sitting on a row with other controls — a count, a
 * percentage, a date.
 *
 * MUI sizes a `TextField` from its input, not from its label, so a compact
 * field in a `direction="row"` Stack collapses to the width of the value it
 * holds and then TRUNCATES ITS OWN LABEL. A one-digit ceiling rendered as
 * `Free w...`, which reads as a broken control rather than a narrow one.
 *
 * Wide enough for a label of a few words at `size="small"`. Named because
 * eleven files hand-write a width in this range and each picked its own, so
 * the console shows the same kind of field at three different sizes; this is
 * the value they should converge on.
 */
export const COMPACT_FIELD_WIDTH = 200

/**
 * The console's table-footer constants, which now live in the shared library
 * (see `@aglyn/shared-ui-jsx/const/table-pagination`).
 *
 * Re-exported rather than moved outright: the console's lists are not the only
 * ones any more — plugin console cards render their own, and a library cannot
 * import from an app. Keeping the console's import path working is what let
 * that move happen without touching every call site.
 */
export {
  TABLE_PAGE_SIZE_DEFAULT,
  TABLE_PAGE_SIZE_OPTIONS,
  TABLE_ROWS_PER_PAGE_LABEL,
} from '@aglyn/shared-ui-jsx/const/table-pagination'

/**
 * Canonical, published legal documents. These are hosted on the production
 * marketing site, not the console, so they are absolute cross-origin URLs
 * opened in a new tab — a full navigation, not in-SPA routing (hence a plain
 * anchor / MUI Link, not AppLink). Always point at production so users see the
 * canonical published terms from every env.
 *
 * ## The operator's own documents, when they have published some (AGL-2017)
 *
 * These were bare `https://aglyn.com/legal/*` literals, and this is the
 * clickwrap: a self-hosted console showed its users a checkbox agreeing to
 * **Aglyn LLC's** Terms and Privacy Policy, for a service Aglyn does not
 * provide. Reading `NEXT_PUBLIC_OPERATOR_LEGAL_ORIGIN` — the variable AGL-2016
 * already defined as "where YOUR published terms/privacy/DMCA pages live" —
 * points the links at whoever actually operates the deployment.
 *
 * ⚠️ THIS IS HALF THE FIX, and the other half is a decision rather than code.
 * `LEGAL_DOCUMENT_VERSION` and the `sha256`/`bytes` triples in
 * `./legal-documents` still identify AGLYN's document snapshots, so the
 * acceptance an operator records still names our bytes even while the link
 * shows theirs. Closing that means deciding what a self-host install should
 * record — the operator's own document identity, or an explicit "no platform
 * agreement" mode that skips the clickwrap — and that is a legal question, not
 * a refactor. AGL-2017 carries it, along with the trap: making the version
 * dynamic turns today's silent degrade into a 500, because
 * `recordLegalAcceptance` throws on a falsy version and the route returns 500.
 * Any dynamic source must keep a non-empty fallback.
 */
/**
 * ⚠️ IMPORTED, NOT READ AGAIN HERE (AGL-2014). This module carried its own
 * `process.env.NEXT_PUBLIC_OPERATOR_LEGAL_ORIGIN || 'https://aglyn.com'` —
 * byte-for-byte the same expression as `published-legal-pages.ts`, on the same
 * variable, with the same default. Two readers of one value is the shape
 * AGL-2195 removed for the tenant apex, and the risk is not that they disagree
 * today: it is that a later fix to one of them (a trailing-slash rule, a second
 * accepted name, an empty-string guard) reaches the clickwrap LINKS while
 * `isPublishedLegalUrl` — the gate that decides whether a publisher agreement
 * URL counts as published — goes on answering from the other. An operator's
 * legal origin cannot be honoured by the links and ignored by the gate.
 */
const LEGAL_ORIGIN = OPERATOR_LEGAL_ORIGIN

export const LEGAL_URLS = {
  TERMS: `${LEGAL_ORIGIN}/legal/terms`,
  PRIVACY: `${LEGAL_ORIGIN}/legal/privacy`,
}

/**
 * Published legal documents the console LINKS to but never asks anyone to
 * accept (AGL-2189).
 *
 * Deliberately separate from {@link LEGAL_URLS}, and the separation is
 * load-bearing rather than tidy. `LEGAL_URLS` is the CLICKWRAP manifest:
 * `legal-document-version.spec.ts` asserts its keys map one-to-one onto the
 * repo-committed snapshots under the `constants/legal` version folders,
 * because — in that
 * spec's words — "a link with no snapshot behind it is the original problem
 * wearing a manifest: the record would name a document it cannot reproduce."
 * The first draft of this change added these two to `LEGAL_URLS` and that
 * spec refused it, correctly.
 *
 * These two are published pages that are deliberately NOT acceptance-pinned
 * (see `./legal-documents`), so they carry no hash and cost no version bump.
 * That is exactly why they can be linked freely, and it is also why they were
 * missing: nothing forced anyone to notice them. The cost of the absence fell
 * on the one audience that needed them — an enterprise reviewer could reach
 * neither the DPA nor the subprocessor list from anywhere in the product,
 * while the trust page told them to email for documents already on a public
 * URL.
 */
export const LEGAL_REFERENCE_URLS = {
  DPA: `${LEGAL_ORIGIN}/legal/dpa`,
  SUBPROCESSORS: `${LEGAL_ORIGIN}/legal/subprocessors`,
  /**
   * The Cookie Policy — the document that names what the console's analytics
   * tag stores and how long for.
   *
   * Belongs in this set rather than in {@link LEGAL_URLS} for the reason
   * stated above: it is published and linked, never accepted, so it carries no
   * clickwrap snapshot and costs no version bump. It is the one document a
   * consent control has to be able to point at — a choice offered with no way
   * to read what is being chosen is not an informed one.
   */
  COOKIES: `${LEGAL_ORIGIN}/legal/cookies`,
}

// The version and content hashes of those documents live in
// `./legal-documents`, which imports LEGAL_URLS from here (AGL-1497).

/**
 * Where an Enterprise enquiry goes (AGL-1118). Enterprise is the one tier with
 * no list price and no self-serve checkout — its card links here instead of an
 * Upgrade button. Same posture as `LEGAL_URLS`: an absolute marketing-site URL
 * opened in a new tab, a full navigation rather than in-SPA routing.
 */
export const ENTERPRISE_CONTACT_URL = `${LEGAL_ORIGIN}/contact?plan=enterprise`

export const mainNavigation = [
  // {
  //   children: 'Features',
  // },
  // {
  //   children: 'Partners',
  //   items: [],
  // },
  // {
  //   children: 'Company',
  //   items: [],
  // },
  {
    children: 'Get Access',
    variant: 'contained',
    color: 'primary',
    href: '/contact',
  },
]
export const footerNavigation = [
  {
    children: 'Resources',
    items: [
      {
        children: 'Get access',
        href: '/contact',
      },
      {
        children: 'Features (coming soon)',
        href: '/features',
        disabled: true,
        'aria-disabled': true,
      },
    ],
  },
  {
    children: 'Company',
    items: [
      {
        children: 'Contact',
        href: '/contact',
      },
    ],
  },
  {
    children: 'Legal',
    items: [
      {
        children: 'Privacy',
        // Absolute, for the reason spelled out on `tailNavigation` below: a
        // console-relative `/legal/privacy` is a 404 here.
        href: LEGAL_URLS.PRIVACY,
        target: '_blank',
        rel: 'noopener',
      },
    ],
  },
]
/**
 * The console footer's links (AGL-2486).
 *
 * ## Both of the two it had were broken, and one of them silently
 *
 * They were written as CONSOLE-RELATIVE paths — `/contact` and
 * `/legal/privacy` — but the console serves neither. `/legal/privacy` was a
 * plain 404. `/contact` was worse: the console's top-level dynamic segment is
 * `[orgSlug]`, so the link resolved to a WORKSPACE named "contact" and
 * rendered a workspace page for an org that does not exist, titled
 * "contact · Aglyn". A 404 tells you the link is wrong; that does not.
 *
 * Every entry is therefore an ABSOLUTE url built from the operator's own
 * origins — `OPERATOR_LEGAL_ORIGIN` and `buildDocsUrl` — never a bare path.
 * A self-hosting operator serves their own legal pages and their own docs
 * (AGL-2091), and a footer that hardcoded ours would send their customers to
 * a company they have no relationship with. That is also why the two
 * commented-out entries below could never simply be uncommented: they had no
 * destination, and `href: '/'` is how they came to be commented out.
 *
 * ## Why these five
 *
 * Docs and Contact are the two a person actually looks in a footer for.
 * Terms and Privacy are the clickwrap pair every acceptance in the product
 * points at. The DPA is here because an enterprise reviewer could otherwise
 * reach it from nowhere in the console — the same absence
 * {@link LEGAL_REFERENCE_URLS} was created to close, which had fixed the
 * links inside the trust page and left the footer as it was.
 *
 * Deliberately NOT here: a status page. `status.aglyn.com` exists and is
 * documented, but it is ours, not an operator's, and there is no configured
 * origin for it — linking it unconditionally would be the self-host bug above
 * in a new place, and adding an env var nothing sets would be a link that
 * never appears. It wants an operator-status origin first.
 */
export const tailNavigation = [
  {
    children: 'Docs',
    /*
     * A GETTER, not a call at module scope (AGL-2486).
     *
     * `buildDocsUrl` lives in `docs-links`, which a dozen specs partially
     * `jest.mock` for `docsHelp` alone. Calling it while this module
     * evaluates therefore threw `buildDocsUrl is not a function` in every one
     * of them — four suites failed to run at all, and none of them is about
     * the footer. Deferring to first read means only a caller that actually
     * renders the footer needs the real module.
     *
     * Resolved through `buildDocsUrl` rather than re-reading
     * `NEXT_PUBLIC_DOCS_ORIGIN` here: `docs-links` documents at length what
     * happened when one docs origin acquired two env names, and a second
     * reader in this file is how the next one starts.
     */
    get href() {
      return buildDocsUrl('/')
    },
    target: '_blank',
    rel: 'noopener',
  },
  {
    children: 'Contact',
    href: `${LEGAL_ORIGIN}/contact`,
    target: '_blank',
    rel: 'noopener',
  },
  {
    children: 'Terms',
    href: LEGAL_URLS.TERMS,
    target: '_blank',
    rel: 'noopener',
  },
  {
    children: 'Privacy',
    href: LEGAL_URLS.PRIVACY,
    target: '_blank',
    rel: 'noopener',
  },
  {
    children: 'DPA',
    href: LEGAL_REFERENCE_URLS.DPA,
    target: '_blank',
    rel: 'noopener',
  },
]
